/**
 * Seletor de idioma (🇧🇷 🇪🇸 🇺🇸) do app do cliente.
 *
 * Só aparece quando a loja ligou algum idioma extra na aba Aparência do
 * painel — loja que não pediu não ganha bandeirinha na tela.
 *
 * Duas formas:
 *  - `variant="compacto"` — botão redondo com a bandeira atual, abre um
 *    menuzinho. Vai no cabeçalho, onde o espaço é disputado.
 *  - `variant="lista"` — as bandeiras lado a lado. Vai no menu lateral,
 *    onde sobra espaço e quem está perdido no idioma errado precisa
 *    encontrar rápido.
 */
import { useEffect, useRef, useState } from 'react';
import { ROTULOS, definirIdioma, idiomaHabilitado, idiomasDisponiveis, useI18n } from '../i18n';

interface Props {
  variant?: 'compacto' | 'lista';
  /** Cabeçalho com banner: fundo escuro translúcido em vez de claro. */
  sobreEscuro?: boolean;
}

export function LanguagePicker({ variant = 'compacto', sobreEscuro = false }: Props) {
  const { lang, t } = useI18n();
  const [aberto, setAberto] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!aberto) return;
    const fora = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setAberto(false);
    };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setAberto(false); };
    document.addEventListener('mousedown', fora);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', fora);
      document.removeEventListener('keydown', esc);
    };
  }, [aberto]);

  if (!idiomaHabilitado()) return null;
  const idiomas = idiomasDisponiveis();

  if (variant === 'lista') {
    return (
      <div>
        <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-2 px-1">{t('Idioma')}</p>
        <div className="flex gap-2">
          {idiomas.map((l) => (
            <button
              key={l}
              type="button"
              onClick={() => definirIdioma(l)}
              aria-pressed={l === lang}
              className={[
                'flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-xl border-2 text-[11px] font-bold transition',
                l === lang
                  ? 'border-red-500 bg-red-50 text-red-700'
                  : 'border-gray-200 text-gray-500 hover:border-gray-300 active:scale-95',
              ].join(' ')}
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <span className="text-base leading-none">{ROTULOS[l].bandeira}</span>
              {ROTULOS[l].rotulo}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="relative" ref={boxRef}>
      <button
        type="button"
        onClick={() => setAberto((v) => !v)}
        aria-label={t('Escolher idioma')}
        aria-expanded={aberto}
        className={[
          'w-9 h-9 flex items-center justify-center rounded-full transition active:scale-95 text-lg leading-none',
          sobreEscuro ? 'bg-black/40 backdrop-blur-sm hover:bg-black/60' : 'hover:bg-black/10',
        ].join(' ')}
        style={{ WebkitTapHighlightColor: 'transparent' }}
      >
        {ROTULOS[lang].bandeira}
      </button>
      {aberto && (
        <div className="absolute right-0 top-11 z-50 bg-white rounded-xl shadow-2xl border border-gray-100 py-1 min-w-[150px] overflow-hidden">
          {idiomas.map((l) => (
            <button
              key={l}
              type="button"
              onClick={() => { definirIdioma(l); setAberto(false); }}
              className={[
                'w-full flex items-center gap-2.5 px-3 py-2.5 text-xs font-bold text-left transition',
                l === lang ? 'bg-red-50 text-red-700' : 'text-gray-700 hover:bg-gray-50',
              ].join(' ')}
            >
              <span className="text-base leading-none">{ROTULOS[l].bandeira}</span>
              {ROTULOS[l].rotulo}
              {l === lang && <i className="fas fa-check ml-auto text-[10px] text-red-500" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
